import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { DiningListItem } from 'voyager';

import { yourJourneySelectors } from '@store/yourJourney';
import type { ParsedJourneyParams } from '@store/yourJourney/selectors';
import rewriteHttp from '@utils/rewriteHttp';

import { diningKeys, useDiningRestaurants, usePropertyContext } from './queries';
import { getRestaurantAvailabilityForDate } from './requests';
import type { DiningAvailabilityTimeslot, DiningRestaurant } from './schemas';

const DEFAULT_GUESTS = 2;

export function mapRestaurantToListItem(restaurant: DiningRestaurant): DiningListItem {
  return {
    id: restaurant.providerId,
    name: restaurant.name,
    label: restaurant.subtitle ?? undefined,
    description: restaurant.description ?? undefined,
    imageUrls: restaurant.imageUrls.map((url) => rewriteHttp(url)),
    menus: restaurant.menus.flatMap((menu) =>
      menu.url ? [{ id: menu.id, title: menu.title, url: rewriteHttp(menu.url) }] : []
    ),
    unavailable: false,
  };
}

export function useDiningList() {
  const { propertyId } = usePropertyContext();
  const { data, isInitialLoading } = useDiningRestaurants();
  const restaurants = Array.isArray(data) ? data : [];

  const journey = useSelector(yourJourneySelectors.getParsedJourneyParams) as ParsedJourneyParams;
  const rawFromDate = useSelector(yourJourneySelectors.getActualFromDateValue);
  const date =
    rawFromDate && journey.fromDate.isValid() ? journey.fromDate.format('YYYY-MM-DD') : undefined;
  const isoDate = date ?? '';
  const adults = journey.tableAdults ?? DEFAULT_GUESTS;
  const children = journey.tableChildren ?? 0;

  const availabilityQueries = useQueries({
    queries: restaurants.map((restaurant) => ({
      queryKey: diningKeys.availability(
        propertyId,
        restaurant.restaurantId,
        restaurant.restaurantName,
        isoDate,
        adults,
        children
      ),
      queryFn: () =>
        getRestaurantAvailabilityForDate(propertyId, {
          date: isoDate,
          restaurantName: restaurant.restaurantName,
          restaurantId: restaurant.restaurantId,
          adults,
          children,
        }),
      enabled: Boolean(date) && propertyId > 0,
    })),
  });

  const availabilityPending =
    Boolean(date) && availabilityQueries.some((query) => query.isInitialLoading);

  const items = availabilityPending
    ? []
    : restaurants
        .map((restaurant, index) => {
          const query = availabilityQueries[index];
          const timeslots: DiningAvailabilityTimeslot[] = query?.data?.timeslots ?? [];
          const unavailable =
            Boolean(date) &&
            Boolean(query?.isSuccess) &&
            !timeslots.some((slot) => slot.hasStandardAvailability);
          return { ...mapRestaurantToListItem(restaurant), unavailable };
        })
        .sort((a, b) => (a.unavailable ? 1 : 0) - (b.unavailable ? 1 : 0));

  return {
    items,
    isLoading: isInitialLoading || availabilityPending,
  };
}
